import { Component, OnInit } from '@angular/core';
import {
    AutoFocusDirective,
    BaseComponent,
    ErrorsUtil,
    InputComponent,
    MessageBoxComponent,
    MessageContent,
    WaitButtonComponent,
    WaitComponent
} from '@sircon/sircon-ui-framework';
import { ActivatedRoute, NavigationEnd, Router, RouterLink } from '@angular/router';
import { each } from 'lodash';
import { CookieService } from 'ngx-cookie-service';
import { filter, switchMap, takeUntil } from 'rxjs';

import { InviteSignupService } from 'src/components/invite-signup/invite-signup.service';
import { UiLoggingHttpService } from 'src/providers/ui-logging-http.service';
import { AuthenticationService } from 'src/services/authentication.service';
import { EnvService } from 'src/services/env.service';
import { HelpService } from 'src/services/help.service';
import { OneTimeAlertService } from 'src/services/one-time-alert.service';
import { UserContextService } from 'src/services/user-context.service';

import { CarrierInformation } from 'src/app/core/models/carrier-information.model';
import { UserAuthenticationRequest, UserAuthenticationResponse } from 'src/app/core/models/login/user-authentication.model';
import { UserContext } from 'src/app/core/models/user/user-context.model';
import { MultiIndustryService } from 'src/services/multi-industry.service';
import { NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BrandInformationComponent } from '../brand-information/brand-information.component';
import { MfaChallengeComponent } from './mfa-challenge/mfa-challenge.component';
import { MfaEnrollmentRequiredComponent } from './mfa-enrollment-required/mfa-enrollment-required.component';

interface MfaChallengeState {
    challengeName: string;
    session: string;
    userName: string;
}

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.less'],
    imports: [WaitComponent, MessageBoxComponent, NgClass, RouterLink, InputComponent,
        AutoFocusDirective, FormsModule, WaitButtonComponent, BrandInformationComponent, MfaChallengeComponent,
        MfaEnrollmentRequiredComponent]
})
export class LoginComponent extends BaseComponent implements OnInit {
    user: { userName?: string; password?: string };
    errors: MessageContent;
    confirmingSso = false;
    invitationAccepted = false;
    invitationNoLongerValid = false;
    invitationWrongUserType = false;
    invitationType: string;
    passwordUpdated = false;
    redirectRoute: string;
    redirectParam: string | string[];
    successMessage: string;
    reload = false;
    cameFromInvite = false;
    fromDocusign = false;
    submitting = false;
    inviteTokenConsumed = false;
    unauthenticatedError = false;
    invalidRecoveryEmailToken = false;
    priorUserId: string;
    loadingBrand = false;
    brandKey: string;
    carrierInformation;
    carrierName: string;
    mfaChallengeState: MfaChallengeState = null;
    mfaEnrollmentRequired = false;
    userNameFromEmailParam = false;

    constructor(
        private window: Window,
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private authenticationService: AuthenticationService,
        private inviteSignupService: InviteSignupService,
        private userContextService: UserContextService,
        private helpService: HelpService,
        private uiLoggingHttpService: UiLoggingHttpService,
        private oneTimeAlertService: OneTimeAlertService,
        private cookieService: CookieService,
        private env: EnvService,
        private multiIndustryService: MultiIndustryService
    ) {
        super();
    }

    ngOnInit(): void {
        this.initializeComponentProperties();
        this.initializePropertiesFromAngularRouter();
        this.initializeUserName();
        this.checkForMfaHandoff();
        this.setTransitionListener();

        // Get parameters from Angular Router
        this.activatedRoute.queryParamMap.pipe(takeUntil(this.ngUnsubscribe)).subscribe(params => {
            this.brandKey = params.get('brandKey');
            this.fromDocusign = params.get('fromDocusign') === 'true';
            this.loadingBrand = !!this.brandKey;

            if (params.get('entityType')) {
                if (params.get('entityType') === 'agency') {
                    // UI-Router: this.stateService.go('agencyLogin', { brandKey, entityType });
                    // Angular Router equivalent:
                    this.router.navigate(['/agencyLogin'], {
                        queryParams: {
                            brandKey: params.get('brandKey'),
                            entityType: params.get('entityType')
                        }
                    });
                }
            }
        });
    }

    initializeComponentProperties(): void {
        this.errors = ErrorsUtil.create();
        this.errors.unexpectedError = this.userContextService.getUnexpectedException();
        this.user = {
            userName: '',
            password: ''
        };
        this.userNameFromEmailParam = false;
    }

    initializeUserName(): void {
        if (this.cameFromInvite) {
            this.user.userName = this.inviteSignupService.getInvite().toEmailAddress;
        } else if (this.confirmingSso) {
            // For Angular Router, we'll get the userName from query params
            this.activatedRoute.queryParamMap.pipe(takeUntil(this.ngUnsubscribe)).subscribe(params => {
                this.user.userName = params.get('userName') ?? this.authenticationService.getJwtSubject();
            });
        } else {
            // Pre-populate email address if provided in query params (lowest precedence)
            this.activatedRoute.queryParamMap.pipe(takeUntil(this.ngUnsubscribe)).subscribe(params => {
                const emailAddress = params.get('emailAddress');
                if (emailAddress && !this.user.userName) {
                    this.user.userName = emailAddress;
                    this.userNameFromEmailParam = true;
                }
            });
        }
    }

    /**
     * Check for MFA challenge handoff from external systems (e.g., SirconWebsite).
     * If valid mfaChallenge cookie exists, show MFA UI immediately without re-authentication.
     * Cookie is kept during pending state to allow refresh/retry; deleted only after success.
     */
    private checkForMfaHandoff(): void {
        const mfaChallengeCookie = this.cookieService.get(this.env.storageKeys.MFA_CHALLENGE);

        if (!mfaChallengeCookie) {
            return;
        }

        try {
            const mfaChallengeData = JSON.parse(mfaChallengeCookie);

            if (!mfaChallengeData.challengeName ||
                !mfaChallengeData.session ||
                !mfaChallengeData.userName) {
                this.deleteMfaChallengeCookie();
                return;
            }

            this.mfaChallengeState = {
                challengeName: mfaChallengeData.challengeName,
                session: mfaChallengeData.session,
                userName: mfaChallengeData.userName
            };

            this.user.userName = mfaChallengeData.userName;

            // Cookie kept for refresh/retry - deleted after successful verification in completeMfaChallenge()

        } catch {
            this.deleteMfaChallengeCookie();
        }
    }

    /**
     * Delete MFA handoff cookie. Called when invalid or after successful verification.
     */
    private deleteMfaChallengeCookie(): void {
        this.cookieService.delete(
            this.env.storageKeys.MFA_CHALLENGE,
            this.env.COOKIES_OPTIONS.path,
            this.env.COOKIES_OPTIONS.domain,
            this.env.COOKIES_OPTIONS.secure,
            this.env.COOKIES_OPTIONS.sameSite
        );
    }

    /**
     * Delete the short-lived MFA-ENROLLMENT cookie once enrollment is complete. Without this, the cookie
     * lingers (unexpired) after enrollment finishes, and the MFA-ENROLLMENT check in completeAuthentication()
     * keeps re-triggering on the subsequent authenticate()/verifyChallenge() responses that follow enrollment -
     * looping the user back into the enrollment screen instead of finishing login.
     */
    private deleteMfaEnrollmentCookie(): void {
        this.cookieService.delete(
            this.env.storageKeys.MFA_ENROLLMENT,
            this.env.COOKIES_OPTIONS.path,
            this.env.COOKIES_OPTIONS.domain,
            this.env.COOKIES_OPTIONS.secure,
            this.env.COOKIES_OPTIONS.sameSite
        );
    }

    initializePropertiesFromAngularRouter(): void {
        this.activatedRoute.queryParamMap.pipe(takeUntil(this.ngUnsubscribe)).subscribe(params => {
            this.unauthenticatedError = params.get('unauthenticated') === 'true' || params.get('sessionTimeout') === 'true';
            this.invitationAccepted = params.get('invitationAccepted') === 'true';
            this.invitationNoLongerValid = params.get('invitationNoLongerValid') === 'true';
            this.inviteTokenConsumed = params.get('inviteTokenConsumed') === 'true';

            if (this.inviteSignupService.getInvite() && this.authenticationService.isAuthenticated()) {
                // Came from invite signup where the user created a new account and authenticated at the end
                this.invitationWrongUserType = params.get('invitationWrongUserType') === 'true';
                this.invitationType = params.get('invitationType');
            }

            this.invalidRecoveryEmailToken = params.get('invalidRecoveryEmailToken') === 'true';
            this.passwordUpdated = params.get('passwordUpdated') === 'true';
            this.redirectRoute = params.get('redirectRoute');
            this.confirmingSso = this.cookieService.check(this.env.storageKeys.PRODUCER_SAML_LOGIN);

            // Handle errors parameter - this might need special handling as it was an object in UI Router
            const errorsParam = params.get('errors');
            if (errorsParam) {
                try {
                    this.errors = JSON.parse(errorsParam);
                } catch {
                    // If parsing fails, create default errors
                    this.errors = ErrorsUtil.create();
                }
            }

            /*
             * When login is coming from the invite flow - where the user account exists and they just need to login.
             * We need some special styling, navigation, and to limit login to the invite email address
             */
            this.cameFromInvite = this.redirectRoute === 'inviteSignup.processInvite';

            // Get all redirectParam values
            const redirectParamValues = params.getAll('redirectParam');

            // redirectParam may arrive either as repeated keys (?redirectParam=a=1&redirectParam=b=2,
            // as sent by external/PM links) or as a single comma-separated value (as some in-app
            // callers send it). getAll() captures every occurrence; splitting each on comma covers both.
            this.redirectParam = redirectParamValues.length
                ? redirectParamValues.flatMap(value => value.split(','))
                : undefined;

            // Fix broken JSON arrays in documentPartyId parameter
            if (this.redirectParam && Array.isArray(this.redirectParam)) {
                this.applyLegacyReconstruction();
            }

            this.successMessage = params.get('successMessage');
            this.reload = params.get('reload') === 'true';
        });
    }

    /**
     * Set up Angular Router navigation listener for reload functionality
     * Triggered by authenticationService.js to force reload on logout to clear all angular and window memory
     */
    setTransitionListener(): void {
        // Angular Router navigation listener - only listen for NavigationEnd events to login route
        // Use switchMap to flatten observables and prevent memory leak from nested subscriptions
        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            filter((event: NavigationEnd) => event.url.includes('/login')),
            switchMap(() => this.activatedRoute.queryParamMap),
            takeUntil(this.ngUnsubscribe)
        ).subscribe(params => {
            if (params.get('reload') === 'true') {
                /*
                 * This timeout is necessary otherwise the login page reloads with unauthenticated = true, which causes
                 * the "Your session has expired" error to appear.  This is most likely due to a race condition
                 * with the routing logic during transition from UI Router to Angular Router.
                 */
                setTimeout(() => {
                    this.reloadWindow();
                }, 1000);
            }
        });
    }

    /**
     * Attempt to authenticate the user.
     * If the user has checked the rememberLoginInfo checkbox then save a cookie with their username
     */
    authenticate(): void {
        this.resetMessages();

        this.submitting = true;
        // Capture the CURRENTLY logged-in user's id (read from the PLM-USER-AUTH cookie) BEFORE
        // authenticating. login() only overwrites that cookie on success, so this still reflects the
        // previous user here. completeAuthentication() compares it against the new user's id to decide
        // whether a genuine user SWITCH happened in this tab - see the reload note there. Undefined when
        // no one was logged in (a normal cold login).
        this.priorUserId = this.authenticationService.getUserId();
        // Safari autofill can override the username even if it is frozen
        this.reinitializeUserNameIfFrozen();
        this.authenticationService.login(this.user as UserAuthenticationRequest)
            .then((response) => this.completeAuthentication(response))
            .catch((errorResponse) => this.handleAuthenticationErrors(errorResponse))
            .finally(() => this.finishAuthentication());
    }

    completeAuthentication(response): void {
        if (this.isMfaChallenge(response)) {
            if (!response.session || response.session === '' || !this.user.userName) {
                this.errors.unexpectedError = true;
                return;
            }

            this.mfaChallengeState = {
                challengeName: response.challengeName,
                session: response.session,
                userName: this.user.userName
            };
            return;
        }

        // Enrollment is required (no MFA enabled yet, but a connected Carrier requires it). Backend signals
        // this via the MFA-ENROLLMENT cookie - AuthenticationHttpService.login() reads that cookie once and
        // stamps mfaEnrollmentRequired onto the response, so this reuses that single read rather than each
        // consumer independently re-checking the cookie. Password expiration takes precedence - see the
        // passwordExpired branches below, reached when this guard is false.
        if (response !== null && !response.passwordExpired && response.mfaEnrollmentRequired) {
            this.mfaEnrollmentRequired = true;
            return;
        }

        // Invite redirects are smart enough to accept an invite when there is a password reset and still show the
        // reset page by adding a passwordExpired param.  Other redirects do not accept the passwordExpired param
        // and would break if they were allowed when there is a password reset.  Note that in addition, the user
        // is not really authenticated when there is a password reset.  This is only ok for invites because
        // invites support a token.
        const usesInviteRedirect = this.redirectRoute === 'inviteSignup.processInvite';

        // Clear out anything previously stored in session storage
        sessionStorage.clear();

        this.oneTimeAlertService.sendAllOneTimeAlerts().pipe(takeUntil(this.ngUnsubscribe)).subscribe();

        if (response !== null && response.passwordExpired && !usesInviteRedirect) {
            // UI-Router: this.stateService.go('expiredPassword', { emailAddress: this.user.userName });
            // Angular Router equivalent:
            this.router.navigate(['/expiredPassword'], {
                queryParams: { emailAddress: this.user.userName }
            });
        } else if (this.redirectRoute) {
            const redirectRouteParams: any = {};
            if (this.redirectParam) {
                each(this.redirectParam, (param) => {
                    const values = param.split('=');
                    redirectRouteParams[values[0]] = values[1];
                });
            }
            if (response !== null && response.passwordExpired) {
                redirectRouteParams.passwordExpired = true;
            } else {
                // Ignoring errors because this is just logging.
                this.uiLoggingHttpService.log({
                    level: 'info', message: 'login redirect to route ' + this.redirectRoute +
                        ' with redirectRouteParams ' + JSON.stringify(redirectRouteParams)
                }).pipe(takeUntil(this.ngUnsubscribe)).subscribe();
            }

            // Convert UI-Router route names to Angular Router paths
            const angularRouterPath = this.convertUiRouterToAngularRoute(this.redirectRoute);

            // UI-Router: this.stateService.go(this.redirectRoute, redirectRouteParams);
            // Angular Router equivalent:
            this.router.navigate([angularRouterPath], {
                queryParams: redirectRouteParams
            });
        } else if (this.confirmingSso) {
            // UI-Router: this.stateService.go('ssoLogin', { samlConfirmationId, token, subscriberId });
            // Angular Router equivalent:
            this.router.navigate(['/ssoLogin'], {
                queryParams: {
                    samlConfirmationId: this.getAndDeleteCookie(this.env.storageKeys.PRODUCER_SAML_LOGIN),
                    token: this.authenticationService.getJwtToken(),
                    subscriberId: this.getAndDeleteCookie(this.env.storageKeys.SUBSCRIBER_ID)
                }
            });
        } else if (response !== null && response.passwordExpired) {
            // UI-Router: this.stateService.go('expiredPassword', { emailAddress: this.user.userName });
            // Angular Router equivalent:
            this.router.navigate(['/expiredPassword'], {
                queryParams: { emailAddress: this.user.userName }
            });
        } else {
            // Default post-login navigation
            const dashboardRoute = this.multiIndustryService.isInsuranceIndustry() ? '/dashboard' : '/dashboard/allActions';

            // When a DIFFERENT user has replaced an active session in this same tab, we must fully reload so
            // the persistent unified header (CSS display, not *ngIf - see app.component.html) re-initializes.
            // It bootstraps the external shui micro-frontend exactly once per page load and loads its alerts
            // from the session on that one-shot init; a fresh sui-user-context event updates header values but
            // does NOT re-fetch alerts, so without a reload the header keeps the previous user's alerts.
            //
            // We gate on a genuine user CHANGE (previous user id != new user id) rather than merely "a session
            // cookie existed", because the far more common case - a same-user re-login after a server-side
            // session timeout - also arrives with the old JWT cookie still present. Reloading there would make
            // every timeout re-login flash a jarring second cold boot for no benefit, since nothing
            // user-scoped is stale. A cold login has no priorUserId, so it never reloads. Once the header can
            // refresh its user-scoped data in place (FOF-7831), this whole reload path can go.
            const newUserId = this.authenticationService.getUserId();
            const isUserSwitch = this.priorUserId && newUserId && this.priorUserId !== newUserId;

            if (isUserSwitch) {
                // Hard-navigate to the dashboard and reload while STILL on the login page. We deliberately do
                // NOT router.navigate() first: an in-app navigation would paint the dashboard with the stale
                // previous-user header for a moment before the reload tears it down. Reloading straight from
                // the login screen (which shows the neutral public header) means the old unified header is
                // never shown. App uses hash routing, so setting the hash targets the dashboard for the fresh
                // boot. See reloadToRoute().
                this.reloadToRoute(dashboardRoute);
            } else {
                // UI-Router: this.stateService.go('dashboard' | 'dashboard.allActions');
                // Angular Router equivalent:
                this.router.navigate([dashboardRoute]);
            }
        }
    }

    handleAuthenticationErrors(errorResponse): void {
        switch (errorResponse.status) {
            case 403:
            case 404:
            case 422:
                if (errorResponse.error && errorResponse.error.fieldErrors) {
                    // Check for LDAP not found error and redirect
                    // fieldErrors is an object with keys like { userName: [{code: 'ldap.not.found', ...}] }
                    let ldapError = false;
                    Object.values(errorResponse.error.fieldErrors).forEach((errors: any) => {
                        if (Array.isArray(errors)) {
                            const hasLdapError = errors.some((error: any) => error.code === 'ldap.not.found');
                            if (hasLdapError) {
                                ldapError = true;
                            }
                        }
                    });

                    if (ldapError) {
                        this.router.navigate(['/resetPasswordInactivity'], { queryParams: { emailAddress: this.user.userName } });
                        return;
                    }

                    this.errors.fieldErrors = errorResponse.error.fieldErrors;
                }
                this.errors.validationError = true;
                break;
            default:
                this.errors.unexpectedError = true;
                break;
        }
    }

    isMfaChallenge(response: UserAuthenticationResponse | UserContext): response is UserAuthenticationResponse {
        return 'challengeName' in response &&
               typeof response.challengeName === 'string' &&
               response.challengeName !== '';
    }

    completeMfaChallenge(response: UserAuthenticationResponse | UserContext): void {
        this.mfaChallengeState = null;
        this.deleteMfaChallengeCookie(); // Delete handoff cookie after successful verification
        this.completeAuthentication(response); // Handles UserAuthenticationResponse or UserContext
    }


    completeMfaEnrollment(): void {
        this.deleteMfaEnrollmentCookie();
        this.authenticationService.getUserContext().then((ctx) => {
            this.mfaEnrollmentRequired = false;
            this.authenticationService.userAuthenticated.emit(ctx);
            this.completeAuthentication({ passwordExpired: false, challengeName: '' });
        });
    }

    freezeUsername(): boolean {
        return this.cameFromInvite ||
               (this.confirmingSso && !!(this.user?.userName)) ||
               this.userNameFromEmailParam;
    }

    reinitializeUserNameIfFrozen() {
        if (this.freezeUsername()) {
            this.initializeUserName();
        }
    }

    finishAuthentication(): void {
        this.submitting = false;
        this.passwordUpdated = false;
    }

    brandedWalkupHelpUrl() {
        return this.helpService.getInsuranceHelpPath();
    }

    /**
     * Reloads the window.  This is split into a separate function for the purposes of unit tests as Jasmine v2.7.0
     * and greater do not allow spying on browser objects (such as window).  A wrapper function is created in order
     * for Jasmine to have something to spy on and prevent the browser reload.
     */
    reloadWindow(): void {
        this.window.location.reload();
    }

    /**
     * Points the (hash-routed) URL at the given app route and forces a full page reload, so the fresh boot
     * lands directly on that route. Used for the user-switch case in completeAuthentication() to reload
     * without first painting the dashboard under the stale previous-user header. Split out (like
     * reloadWindow) so tests can spy on it and avoid an actual browser reload.
     */
    reloadToRoute(route: string): void {
        this.window.location.hash = route;
        this.window.location.reload();
    }

    goBack(): void {
        this.window.history.back();
    }

    loadingBrandEvent(loading: boolean): void {
        this.loadingBrand = loading;
    }

    loadingBrandErrorEvent(): void {
        this.errors.serviceUnavailable = true;
    }

    carrierInformationEvent(carrierInformation: CarrierInformation): void {
        this.carrierInformation = carrierInformation;
        this.carrierName = carrierInformation.carrierName;
    }

    /**
     * Clear out success and error messages
     */
    resetMessages(): void {
        delete this.successMessage;
        delete this.invitationAccepted;
        delete this.passwordUpdated;
        delete this.invitationNoLongerValid;
        delete this.invitationWrongUserType;
        delete this.invitationType;
        delete this.inviteTokenConsumed;
        delete this.invalidRecoveryEmailToken;
        this.errors = ErrorsUtil.create();
    }

    getAndDeleteCookie(name: string) {
        const value = this.cookieService.get(name);
        this.cookieService.delete(
            name,
            this.env.COOKIES_OPTIONS.path,
            this.env.COOKIES_OPTIONS.domain,
            this.env.COOKIES_OPTIONS.secure,
            this.env.COOKIES_OPTIONS.sameSite);
        return value;
    }

    /**
     * Convert UI-Router state names to Angular Router paths
     * This method handles the mapping from UI-Router state names to Angular Router paths
     *
     * IMPORTANT: Strips query parameters before mapping to prevent query param pollution
     * Example: 'dashboard?type=context_timeout' -> 'dashboard' -> '/dashboard'
     */
    private convertUiRouterToAngularRoute(uiRouterState: string): string {
        // Strip query parameters if present (e.g., 'dashboard?type=context_timeout' -> 'dashboard')
        const cleanState = uiRouterState.split('?')[0];

        // Map of UI-Router state names to Angular Router paths
        // This mapping handles backward compatibility for old email links and bookmarks
        const routeMapping: { [key: string]: string } = {
            // Dashboard routes
            'dashboard': '/dashboard',
            'dashboard.activity': '/dashboard/activity',
            'dashboard.allActivity': '/dashboard/allActivity',
            'dashboard.allActions': '/dashboard/allActions',
            'dashboard.carrierProfile.activity': '/dashboard/carrierProfile/activity',
            'dashboard.carrierProfile.activity.beneficiaries': '/dashboard/carrierProfile/activity/beneficiaries',
            'dashboard.carrierRequest': '/dashboard/carrierRequest',
            'dashboard.compliance': '/dashboard/compliance',
            'dashboard.compensation': '/dashboard/compensation',
            'dashboard.entityRecord': '/dashboard/entityRecord',
            'dashboard.globalActivities': '/dashboard/globalActivities',
            'dashboard.insurance': '/dashboard/insurance',
            'dashboard.invoices': '/dashboard/invoices',
            'dashboard.licenses': '/dashboard/licenses',
            'dashboard.payments': '/dashboard/payments',
            'dashboard.paymentPreferenceEmailRedirect': '/dashboard/paymentPreferenceEmailRedirect',
            'dashboard.paymentPreferenceTaskRedirect': '/dashboard/paymentPreferenceTaskRedirect',
            'dashboard.personalSummaryScheduler': '/dashboard/personalSummaryScheduler',
            'dashboard.policyTaskRedirect': '/dashboard/policyTaskRedirect',
            'dashboard.producerWorkflow': '/dashboard/producerWorkflow',
            'dashboard.relationship': '/dashboard/relationship',
            'dashboard.securities': '/dashboard/securities',

            // Network routes
            'network': '/network',
            'network.carrierAgencyProfile.carrierRequest': '/network/carrierAgencyProfile/carrierRequest',
            'network.entities': '/network/entities',
            'network.home': '/network/home',

            // Admin routes
            'admin.users': '/admin/users',

            // Account routes
            'accountSettings': '/accountSettings',

            // Workflow routes
            'filingWorkflow': '/filingWorkflow',
            'interview': '/workflow',
            'manageBeneficiariesWorkflow': '/manageBeneficiariesWorkflow',

            // Disclosure routes
            'disclosureSummary': '/disclosureSummary',
            'gneSummary': '/gneSummary',

            // Redirect helpers
            'documentSharedNotificationRedirect': '/documentSharedNotificationRedirect',
            'producerContractRedirect': '/producerContractRedirect',

            // Subscription routes
            'subscriptionUpgradeBenefits': '/subscriptionUpgradeBenefits',

            // Invite signup routes
            'inviteSignup.processInvite': '/inviteSignup/processInvite',
            'inviteSignup.signup': '/inviteSignup/signup',
            'inviteSignup.welcome': '/inviteSignup/welcome',
            'inviteSignup.complete': '/inviteSignup/complete',

            // Reporting routes - UI-Router state names were kebab (reporting-interactive) but the
            // Angular paths are slash-nested (reporting/interactive). Without these, a post-login
            // redirectRoute=reporting-interactive fell back to '/reporting-interactive' -> /error.
            'reporting': '/reporting',
            'reporting-interactive': '/reporting/interactive',
            'reporting-standard': '/reporting/standard',
            'reporting-pentaho': '/reporting/pentaho',

            // Authentication routes
            'expiredPassword': '/expiredPassword',
            'ssoLogin': '/ssoLogin',
            'login': '/login',

            // Common fallbacks
            'home': '/dashboard',
            'main': '/dashboard'
        };

        // Return mapped route or fallback to convert dot notation to slash notation
        // Use cleanState (without query params) for mapping lookup and fallback
        return routeMapping[cleanState] || `/${cleanState.replace(/\./g, '/')}`;
    }

    /**
     * Fix broken JSON arrays in documentPartyId parameter.
     *
     * When isMultipleDocuments=true, documentPartyId contains a JSON array like ["id1","id2"].
     * The comma-splitting in getAll().flatMap(value => value.split(',')) breaks this array
     * into multiple parameters. This method reconstructs the full JSON array.
     */
    private applyLegacyReconstruction(): void {
        const isMultipleDocuments = (this.redirectParam as string[]).some(param => param === 'isMultipleDocuments=true');
        if (!isMultipleDocuments) {
            return; // Early exit if not multiple documents
        }

        const documentPartyIdIndex = (this.redirectParam as string[]).findIndex(param => param.startsWith('documentPartyId'));
        if (documentPartyIdIndex === -1) {
            return; // No documentPartyId found
        }

        try {
            // Reconstruct the full documentPartyId value by collecting parts until we have valid JSON
            const reconstructedValue = (this.redirectParam as string[])[documentPartyIdIndex];
            const valueParts = reconstructedValue.split('=').slice(1);
            let fullValue = valueParts.join('='); // Handle cases where value contains '='

            // Check if the value looks like the start of a JSON array
            const decodedCheck = decodeURIComponent(fullValue);
            if (!decodedCheck.trim().startsWith('[')) {
                // Not a JSON array, might already be properly formatted
                return;
            }

            // Collect subsequent params that are part of the broken JSON array
            let currentIndex = documentPartyIdIndex + 1;
            while (currentIndex < (this.redirectParam as string[]).length && !fullValue.trim().endsWith(']')) {
                const nextParam = (this.redirectParam as string[])[currentIndex];
                fullValue += ',' + nextParam;
                currentIndex++;
            }

            // Decode and parse the JSON array
            const decodedValue = decodeURIComponent(fullValue);
            const documentPartyIdArray = JSON.parse(decodedValue);

            // Validate that it's actually an array
            if (!Array.isArray(documentPartyIdArray) || documentPartyIdArray.length === 0) {
                console.warn('applyLegacyReconstruction: documentPartyId is not a valid array', decodedValue);
                return;
            }

            // Re-encode for routing
            const documentPartyIdJsonString = JSON.stringify(documentPartyIdArray);
            const urlEncodedDocumentPartyId = encodeURIComponent(documentPartyIdJsonString);

            // Remove the broken parts and replace with the corrected param
            const partsToRemove = currentIndex - documentPartyIdIndex;
            (this.redirectParam as string[]).splice(documentPartyIdIndex, partsToRemove, `documentPartyId=${urlEncodedDocumentPartyId}`);
        } catch (error) {
            console.error('applyLegacyReconstruction: Failed to reconstruct documentPartyId', error);
            // Leave redirectParam as-is if reconstruction fails
        }
    }
}
